import { Inject } from '@odinjs/odin';
import { BarcodeAPI, BarcodeService as BarcodeApiService, BarcodeData, BarcodeReaderType, ButtonAPI, DeckAPI, FieldAPI, ModalButton, ModalProps, ModalService, TextAreaAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, HttpStatus } from '@philips-internal/oswald-ui-core/lib/services';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/message-service';

import { applicationContext, CredentialsValidationError, CredentialsValidationSuccess, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID, isFunction } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';

import { Endpoint } from '../../../../constants/endpoints';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import BaseController from '../../../BaseController';
import { AdminContextDataProps, AdminModalsService } from '../../services/admin-modals-service';
import { AdminByScheduleParams } from '../administration-schedule/administration-schedule-params';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { AdminByProcessParams } from './medication-administration-by-process-params';
import BarcodeService from '../../../../services/barcode-service'

import './medication-administration-by-process-controller.css';
import { ChangeType, OptionCheck } from '../../../../constants/infusion-solutions';
import { BodyCheckInfo } from '../medication-viewer/medication-viewer-types';
import { AdmMedicationSolutionByProcess } from './medication-administration-by-process-types';
import MedicationService, { CreateEventMedicationConferenceRequest, CreateFirstMedicationCheckRequest } from '../../services/medication-service';
import { PrescriptionType } from '../../services/timeline-service';
import { Conditionals, DisplayMessages, PrescriptionStatus } from '../administration-schedule/administration-schedule-types';
import InfusionService, { CreateFirstSolutionCheckRequest } from '../../services/infusion-service';
import { SolutionType } from '../../adjust-infusion-speed/adjust-infusion-speed-types';
import { FragmentNames, MedicationAdministrationByProcessAliases } from '../../../../codegen';

interface ResponseBodyProps {
  hourDate?: string;
  message?: string ;
  success?: boolean;
  total?: number;
  totalRead?: number;
  items?: Record<string, Value>[];
}

interface JWTToken {
    [key: string]: unknown;
    sub: string;
  }

const INDEX_ARRAY_FIRST_POSITION = 0;
const NUMBER_ONE = 1;

@Controller({ component: MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_LAYOUT, fragment: FragmentNames.MEDICATION_ADMINISTRATION_BY_PROCESS })
class MedicationAdministrationByProcessController extends BaseController {

  @Inject(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_DECK)
  private administrationProcessDeckApi!: DeckAPI;

  @Inject(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_HOUR_FIELD)
  private administrationProcessHourFiledApi!: FieldAPI;

  @Inject(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_BARCODE)
  private readPenddingBarcodeApi!: BarcodeAPI;

  @Inject(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_BUTTON)
  private administrationProcessButtonApi!: ButtonAPI;

  @Inject(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_JUSTIFICATION_TEXT_AREA)
  private administrationProcessTextAreaApi!: TextAreaAPI;

  showToast(message: string, typeMessage: ToastLevel): void {
    ToastService.open({
      dismissExpression: message,
      dismissible: true,
      highlight: false,
      id: 'administration-by-process',
      level: typeMessage,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  private isSolution(): boolean {
    const { medicationOrSolution } = this.context.get('adminContextData') as AdminContextDataProps;
    return medicationOrSolution === 'solution' ? true : false;
  }

  resetAdministrationProcess = (barcodeValue: string): void => {
    const { processSequence } = this.context.get('adminContextData') as AdminContextDataProps;
    const body = {
        barcode: barcodeValue,
        processSequence,
        items: this.administrationProcessDeckApi.getData()
    };

    HttpService.post(Endpoint.EMAR_RESET_ONE_PROCESS, {}, { body } ).then((resp) => {
      const response = resp.body as  Record<string, Value>[] as ResponseBodyProps;
      const message = response.message;
      const totalRead = response?.items?.filter(((item) => item.read === true));
      const withoutReading = response?.items?.filter(((item) => item.read === false));
      const isRead = totalRead?.length && totalRead?.length > 0;

      this.showToast(String(message), ToastLevel.SUCCESS);

      if (response.items) {
        this.context.set({ items: response });
        this.administrationProcessDeckApi.setData(response.items);
        AdminByScheduleParams.readParam = { noCardsRead: false };

        if (withoutReading?.length && withoutReading?.length > 0) {
            this.readPenddingBarcodeApi.setEnabled(true);
            this.context.set({ readPendding: false });
            AdminByScheduleParams.readParam = { noCardsRead: true };
        }
        this.administrationProcessButtonApi.setEnabled(isRead as boolean);
      }
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      this.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  private async credentialValidationUser(items: AdmMedicationSolutionByProcess[]): Promise<void> {
    const configPrompt = {
      onDismiss: () => {
        this.administrationProcessButtonApi.setEnabled(true);
      },
      onError: (event: CredentialsValidationError) => this.validateCredentialsWithError(event),
      onSuccess: (event: CredentialsValidationSuccess) => this.validateCredentialsWithSuccessHandler(event, true, items),
      validateButtonExpression: MessageService.translate(DisplayMessages.VALIDATE_BUTTON_EXP),
    }

    foundationContext.promptCredentialsValidation(configPrompt);
  }

  private getBodyCheckInfo(data: AdmMedicationSolutionByProcess, secondUser: string, isSolution: boolean): BodyCheckInfo {
    const option = secondUser ? OptionCheck.SAME_USER : OptionCheck.FIRST_CHECK;
    const userName = secondUser ? secondUser : foundationContext.getUser()?.username || '';

    return isSolution
      ? {
        itemCode: Number(data.itemCode),
        itemType: PrescriptionType.SOLUTION,
        option,
        prescriptionNumber: String(data.prescriptionNumber),
        scheduleSequence: data.step,
        userName,
      }
      : {
        itemCode: data.materialCode,
        itemType: PrescriptionType.MEDICINE,
        option,
        prescriptionNumber: String(data.prescriptionNumber),
        scheduleSequence: data.tagNumber,
        userName: userName,
      }
  }

  private handleProccess(): void {
    const loadedItems = this.context.get('loadedItems') as AdmMedicationSolutionByProcess[];
    const doubleCheckItems = loadedItems.filter(item => item.isDoubleCheck === Conditionals.POSITIVE && item.read);
    if (doubleCheckItems?.length) {
      this.handleProccessDoubleCheck(doubleCheckItems);
    } else {
      this.confirmUnfolding();
    }
  }

  private async handleProccessDoubleCheck(doubleCheckItems: AdmMedicationSolutionByProcess[]): Promise<void> {
    const isSolution = this.isSolution();

    for (const item of doubleCheckItems) {
      if (item.status !== PrescriptionStatus.CHECKED_FIRST) {
        const isFirstCheck = isSolution
          ? await InfusionService.validateStatusMedication(OptionCheck.FIRST_CHECK, this.getBodyCheckInfo(item, '', isSolution))
          : await MedicationService.validateStatusItem(this.getBodyCheckInfo(item, '', isSolution));
        if (isFirstCheck) {
          const firstCheckCreated = await this.createFirstCheckItem(item, isSolution);
          item.firstCheckCreated = firstCheckCreated;
        }
      }
    }

    this.credentialValidationUser(doubleCheckItems);
  }

  private createEventMedicationConference(item: AdmMedicationSolutionByProcess, isSolution: boolean, userNameSecondUserValidated: string): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const userName = userNameSecondUserValidated || foundationContext.getUser()?.username;
    const request: CreateEventMedicationConferenceRequest = {
      changeStatus: isSolution ? ChangeType.CONFERENCE_SOLUTION : ChangeType.CONFERENCE_MEDICATION,
      encounterNumber: Number(encounterId),
      glucoseProteinSequence: 0,
      internalProcessSequence: 0,
      changeDate: moment().format(ISO_FULL_DATE_FORMAT),
      itemCode: item.materialCode,
      itemSequence: NUMBER_ONE,
      itemType: isSolution ? PrescriptionType.SOLUTION : PrescriptionType.MEDICINE,
      justifyDescription: item.observation,
      pdoPrn: Conditionals.NEGATIVE,
      prescriptionNumner: item.prescriptionNumber,
      scheduleSequence: item.tagNumber,
      userName
    }

    MedicationService.createEventMedicationConference(request);
  }

  private createFirstCheckItem(item: AdmMedicationSolutionByProcess, isSolution: boolean): Promise<boolean> {
    const request = isSolution
      ? {
        actionType: ChangeType.START,
        infusionType: SolutionType.SOLUTION,
        itemSequence: item.itemCode,
        prescriptionNumber: item.prescriptionNumber,
        scheduleSequence: item.tagNumber,
        userName: ''
      }
      : {
        encounterNumber: item.encounterNumber,
        eventDate: item.hourDate,
        itemType: PrescriptionType.MEDICINE,
        justificationDescription: item.observation,
        scheduleSequence: item.tagNumber
      };

    return isSolution
      ? this.createFirstInfusionCheck(request as CreateFirstSolutionCheckRequest)
      : this.createFirstMedicationCheck(request as CreateFirstMedicationCheckRequest);
  }

  private async createFirstMedicationCheck(request: CreateFirstMedicationCheckRequest): Promise<boolean> {

    return MedicationService.createFirstMedicationCheck(request)
      .then((res) => {
        MedicationService.showToast(DisplayMessages.FIRST_MED_CHECK_CREATED, ToastLevel.SUCCESS);
        const created = res.created === Conditionals.POSITIVE;
        return created;
      }).catch(response => {
        const body = response.body;
        const hasErrors = !!body?.errors;
        if (hasErrors) {
          body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
        return Promise.resolve(false);
      });
  }

  private async createFirstInfusionCheck(request: CreateFirstSolutionCheckRequest): Promise<boolean> {

    return await InfusionService.createFirstInfusionCheck(request)
      .then((status) => {
        if (status >= HttpStatus.OK && status < HttpStatus.MULTIPLE_CHOICES) {
          MedicationService.showToast(DisplayMessages.FIRST_MED_CHECK_CREATED, ToastLevel.SUCCESS);
          return true;
        }
        return false;
      }).catch(response => {
        const body = response.body;
        const hasErrors = !!body?.errors;
        if (hasErrors) {
          body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
        return false;
      });
  }

  private confirmUnfolding(doubleCheckPerformed?: boolean, userNameSecondUserValidated?: string): void {
    const { processSequence, medicationOrSolution } = this.context.get('adminContextData') as AdminContextDataProps;
    const justification = this.administrationProcessTextAreaApi.getValue();
    const userName = userNameSecondUserValidated || foundationContext.getUser()?.username;

    const body = {
      administrationDate: moment().format(ISO_FULL_DATE_FORMAT),
      isSolution: medicationOrSolution === 'solution' ? 'solution' : null,
      items: this.administrationProcessDeckApi.getData(),
      justification: justification === '' ? null : justification,
      processSequence,
      userName
    }

    HttpService.post(Endpoint.EMAR_CONSIST_MANAGE_PROCESS, {}, { body })
      .then((res) => {
        const responseBody = res.body as Record<string, Value>[] as ResponseBodyProps;

        if (responseBody?.message === 'isSolution') {
          const adminType = 'infusion-admin-schedule'
          AdminByProcessParams.admType = { isAdmProcess: true };
          AdminModalsService.modalAdminByProcessOrScheduleIsVisible(String(adminType), {}, doubleCheckPerformed);
          this.administrationProcessButtonApi.setEnabled(true);
        } else {
          this.showToast(String(responseBody?.message), ToastLevel.SUCCESS);
          MedicineAndSolutionsParams.params = { modalIsVisible: 'N' };
          ModalService.close('medication-administration-by-process');
          applicationContext.popNavigationItem();
        }
      })
      .catch((error): void => {
        if (error?.body?.errors?.length > 0) {
          for (const err of error.body.errors) {
            this.showToast(err, ToastLevel.ERROR);
          }
        } else if (error?.body?.message) {
          this.showToast(error.message, ToastLevel.ERROR);
        }
        this.administrationProcessButtonApi.setEnabled(true);
      });

    this.administrationProcessTextAreaApi.setValue('');
  }

  private dialogModalConfirmUnfolding(): void {
    const buttons: ModalButton[] = [];
    const { processSequence } = this.context.get('adminContextData') as AdminContextDataProps;
    const message = MessageService.translate('text.cofirm-the-unfolding-of-process').replace('#@NR_PROCESSO#@', String(processSequence));

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.handleProccess(),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: message,
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  private consistAdministrationByProcess(): void {
    const readPendding = this.context.get('readPendding');

    if (readPendding) {
      this.handleProccess();
    } else {
      this.dialogModalConfirmUnfolding();
    }
  }

  private async validateSecondUser(firstItem: AdmMedicationSolutionByProcess, event: CredentialsValidationSuccess, isSolution: boolean): Promise<string | undefined> {
    const userValidateCredentials = this.getSecondUserName(event);
    const userSecondCheckInvalid = await MedicationService.statusMedicationCheck(
      this.getBodyCheckInfo(firstItem, userValidateCredentials, isSolution),
      true
    );

    return userSecondCheckInvalid?.responseDescription;
  }

  private getSecondUserName(event: CredentialsValidationSuccess): string {
    const payloadSymbol = Object.getOwnPropertySymbols(event.token).find(s => s.description === 'payload');
    const userValidateCredentials = payloadSymbol && ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol]
      ? ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol].sub
      : ' ';
    return userValidateCredentials as string;
  }

  private async processSecondCheck(items: AdmMedicationSolutionByProcess[], isSolution: boolean, event: CredentialsValidationSuccess): Promise<void> {
    const firstItem = items[INDEX_ARRAY_FIRST_POSITION];
    const userSecondCheckInvalid = await this.validateSecondUser(firstItem, event, isSolution);

    if (userSecondCheckInvalid) {
      this.invalidUserToSecondCheck(userSecondCheckInvalid);
      this.credentialValidationUser(items);
      return;
    }

    this.administrationProcessButtonApi.setEnabled(false);

    const userNameSecondUserValidated = this.getSecondUserName(event);
    for (const item of items) {
      if (item.firstCheckCreated || item.status === PrescriptionStatus.CHECKED_FIRST) {
        await this.createEventMedicationConference(item, isSolution, userNameSecondUserValidated);
      }
    }

    this.confirmUnfolding(true, userNameSecondUserValidated);
  }

  private async validateCredentialsWithSuccessHandler(event: CredentialsValidationSuccess, doubleCheck: boolean, items: AdmMedicationSolutionByProcess[] | undefined): Promise<void> {
    if (doubleCheck && items) {
      const isSolution = this.isSolution();
      await this.processSecondCheck(items, isSolution, event);
      return;
    }

    this.confirmUnfolding();
  }

  private invalidUserToSecondCheck(message: string): void {
    MedicationService.showToast(message, ToastLevel.ERROR);
  }

  remainingTries = 3;

  private validateCredentialsWithError(event: CredentialsValidationError): void {
    const { abort } = event;

    this.showToast(MessageService.translate('login-message.invalid-user-password-text'), ToastLevel.ERROR);

    if (this.remainingTries > 0) {
      this.remainingTries = this.remainingTries - 1;
    }

    if (this.remainingTries === 0) {
      abort();
    }
  }

  private validateCredentials(): void {
    foundationContext.promptCredentialsValidation({
      onDismiss: () => {},
      onError: (event) => this.validateCredentialsWithError(event),
      onSuccess: (event) => this.validateCredentialsWithSuccessHandler(event, false, undefined),
      validateButtonExpression: MessageService.translate('$expression.$702951'),
    });
  }

  private verifyAdepParamFortyTwo(): void {
    const adepTasyFunction = 'ADEP#1113';
    const adepTasyName = 'REQUIRE_USER_CONFIRMATION_BY_BARCODE#42';
    const paramTwsOrTasy = FoundationUtils.findModuleParam(adepTasyFunction, adepTasyName);

    if (paramTwsOrTasy === 'N') {
      this.validateCredentials();
    } else {
      this.consistAdministrationByProcess();
    }
  }

  private validatePatientByBarcode(event: ReadEvent<BarcodeData>): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const barcodeValue = event.item.value.trim();

    if (encounterId === barcodeValue) {
      this.showToast(MessageService.translate('text.patient-confirmed-with-success'), ToastLevel.SUCCESS);
      this.verifyAdepParamFortyTwo();
    } else {
      this.showToast(MessageService.translate('text.this-bracelet-does-not-belong-to-this-service'), ToastLevel.ERROR);
      this.administrationProcessButtonApi.setEnabled(true);
    }
  }

  openBarcodeModalService(): void {
    BarcodeApiService.open({
      data: [],
      defaultReadType: BarcodeReaderType.MANUAL,
      hasDescription: false,
      id: 'admin-by-process-modal-service',
      manualInputBarcodeExpression: 'tws.barcode.wristband',
      onReadSuccess: (event: ReadEvent<BarcodeData>) => {
        this.validatePatientByBarcode(event);
      },
      requireDescription: false,
      requireReason: false,
      supportCamera: true,
      supportInfrared: true
    });
  }

  private searchAllMedicationsOrSolutionsPendding = (): Promise<Record<string, Value>[]> => {
    const param14 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.LABEL_TO_BE_READ_TO_CONFIRM_INSTALLATION_OF_SOLUTIONS);
    const { processSequence, medicationOrSolution } = this.context.get('adminContextData') as AdminContextDataProps;

    const params = {
      labelStatus: medicationOrSolution === 'solution' ? param14 : null,
      processSequence,
    }

    return HttpService.get(Endpoint.EMAR_GET_ALL_PROCESS, params).then((res) => {
      const responseBody = res.body as Record<string, Value>[];

      return responseBody;
    });
  }

  @OnMount({ target: 'administration-by-process-layout' })
  onMount(): void {
    this.administrationProcessDeckApi.setLoading(true);
    this.searchAllMedicationsOrSolutionsPendding().then((response) => {
        const responseBody = response as Record<string, Value>[];
        const maxCards = 11;
        this.administrationProcessDeckApi.setDone(true);

        if (responseBody?.length > maxCards) {
            this.administrationProcessDeckApi.setDone(false);
        }

        if (responseBody?.length > 0) {
            const dateProcess = responseBody[0].hourDate;
            const dateTimeFormated = moment(String(dateProcess)).format('L LTS');
            this.context.set({ loadedItems: responseBody });
            this.administrationProcessDeckApi.setData(responseBody);
            this.administrationProcessHourFiledApi.setData({ hourDate: dateTimeFormated });
        }
    }).catch((error) => {
        const errorMessage = error.body.errors[0];
        this.showToast(errorMessage, ToastLevel.ERROR);
    }).finally(() => this.administrationProcessDeckApi.setLoading(false));

    AdminByScheduleParams.readParam = { noCardsRead: true };
  }

  @OnMount(MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess({ target: MedicationAdministrationByProcessAliases.ADMINISTRATION_BY_PROCESS_BARCODE })
  onReadSuccess(event: ReadEvent<BarcodeData>): void {
    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }

    const { processSequence } = this.context.get('adminContextData') as AdminContextDataProps;

    const body = {
      barcode: event.item.value.trim(),
      processSequence,
      items: this.administrationProcessDeckApi.getData(),
    }

    HttpService.post(Endpoint.EMAR_CONFIRM_BARCODE, {}, { body })
      .then((response) => {
        const { message, total, totalRead, items } = response.body as Record<string, Value>[] as ResponseBodyProps;
        const isValidaData = total === totalRead;
        this.context.set({loadedItems: items as Record<string, Value>[]});
        this.administrationProcessDeckApi.setData(items as Record<string, Value>[]);

        if (message) {
          this.showToast(message, ToastLevel.SUCCESS);
        }

        if (isValidaData) {
          this.readPenddingBarcodeApi.close();
        }

        if (Number(totalRead) > 0) {
          this.administrationProcessButtonApi.setEnabled(true);
          AdminByScheduleParams.readParam = { noCardsRead: false };
        }

        this.readPenddingBarcodeApi.setEnabled(!isValidaData);
        this.context.set({ readPendding: isValidaData });
      }).catch((error) => {
        const errorMessage = error.body?.errors[0];
        this.showToast(errorMessage, ToastLevel.ERROR);
      })
  }

  @OnClick({ target: 'administration-by-process-card' })
  onClickResetButton(event: ClickEvent): void {
    const [, barcodeValue] = event.value.barcode.split(',');

    BarcodeUtils.dialogModalConfirm(barcodeValue, this.resetAdministrationProcess);
  }

  @OnClick({ target: 'administration-by-process-button' })
  onClickRealizeProcess(): void {
    const adepTasyName = 'REQUIRE_PATIENT_CONFIRMATION_WHEN_ADMINISTERING_PROCESS#9';
    const paramTwsOrTasy = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, adepTasyName);

    this.administrationProcessButtonApi.setEnabled(false);

    if (paramTwsOrTasy === 'N') {
      this.verifyAdepParamFortyTwo();
      return;
    }

    this.openBarcodeModalService();
  }
}

export default MedicationAdministrationByProcessController;
